/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
/** @jsx jsx */

import React, {
	type ComponentPropsWithoutRef,
	createContext,
	useContext,
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import type { PreloadedEntryPoint } from 'react-relay';
import {
	useSsrPlaceholders,
	useIsHydrateRenderMode,
} from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { cssMap, jsx } from '@atlaskit/css';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { JSResourceReference } from '@atlassian/react-async';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import type { LazySuspenseProps } from '@atlassian/react-loosely-lazy/suspense/types';

const styles = cssMap({
	root: {
		display: 'contents',
	},
});

export type EntryPoint = {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	root: JSResourceReference<any>;
};

type Placeholders = Map<string, string>;

export function getPlaceholdersFromHTML(): Map<string, string> {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const placeholders = document.querySelectorAll<HTMLElement>('[data-ep-placeholder-id]');

	// oxlint-disable-next-line typescript/no-non-null-assertion
	return new Map(Array.from(placeholders, (el) => [el.dataset.epPlaceholderId!, el.innerHTML]));
}

export const EntryPointPlaceholderContext: React.Context<Placeholders> =
	createContext<Placeholders>(new Map());

export type EntryPointReferencePlaceholderProps = Omit<
	ComponentPropsWithoutRef<typeof Placeholder>,
	'name'
> & {
	name: string;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	entryPointReference: PreloadedEntryPoint<any>;
	/**
	 * If the placeholder wraps a page layout component, we add a data-layout-slot="true" attribute.
	 * This matches what the Root component in `/atlassian-frontend-monorepo/platform/packages/design-system/navigation-system/src/ui/page-layout/root.tsx`
	 * expects to find on all of the page-layout components.
	 */
	isPlaceholderForPageLayoutComponent?: boolean;
	isSSRPlaceholderEnabled?: boolean;
};

/**
 * Entry point placeholder component without content preservation.
 * Uses the fallback from props directly without attempting to preserve SSR content.
 */
function NoContentPreservationEntryPointReferencePlaceholder({
	children,
	entryPointReference,
	fallback,
	name,
	isPlaceholderForPageLayoutComponent: _isPlaceholderForPageLayoutComponent,
	isSSRPlaceholderEnabled: _isSSRPlaceholderEnabled,
	...placeholderProps
}: EntryPointReferencePlaceholderProps) {
	return (
		<Placeholder fallback={fallback} name={name} {...placeholderProps}>
			{children}
		</Placeholder>
	);
}

/**
 * Entry point placeholder component with content preservation.
 * Preserves SSR-rendered content from the context until the component is ready.
 */
function EntryPointReferencePlaceholderWithContentPreservation({
	children,
	entryPointReference,
	fallback: originalFallback,
	name,
	isPlaceholderForPageLayoutComponent = false,
	isSSRPlaceholderEnabled,
	...placeholderProps
}: EntryPointReferencePlaceholderProps) {
	const hasMounted = useRef(false);
	const displaySsrPlaceholderAttrs = useSsrPlaceholders();

	// using useState to ensure the entryPointReference is loaded synchronously
	// for the initial render (enabling SSR)
	const [component] = useState(() => {
		if (entryPointReference) {
			try {
				return entryPointReference.getComponent();
			} catch (thrown: unknown) {
				return undefined;
			}
		}
	});

	const placeholders = useContext(EntryPointPlaceholderContext);
	const ssrFallback = placeholders.get(name);
	const [fallback, setFallback] = useState(
		!ssrFallback || component ? (
			originalFallback
		) : (
			<span
				// oxlint-disable-next-line react/no-danger
				dangerouslySetInnerHTML={{ __html: ssrFallback }}
				css={styles.root}
				data-ep-placeholder-id={name}
				{...(isPlaceholderForPageLayoutComponent && { 'data-layout-slot': 'true' })}
				{...(isSSRPlaceholderEnabled && {
					'data-vc': name,
				})}
				{...(isSSRPlaceholderEnabled &&
					displaySsrPlaceholderAttrs &&
					__SERVER__ && {
						'data-ssr-placeholder': name,
					})}
				{...(isSSRPlaceholderEnabled &&
					displaySsrPlaceholderAttrs &&
					!__SERVER__ && {
						'data-ssr-placeholder-replace': name,
					})}
			/>
		),
	);

	const lastOriginalFallback = useRef(originalFallback);
	lastOriginalFallback.current = originalFallback;

	const resetFallback = useCallback(() => {
		if (placeholders.has(name)) {
			placeholders.delete(name);
			setFallback(lastOriginalFallback.current);
		}
	}, [name, placeholders, setFallback]);

	useLayoutEffect(() => {
		// reset the fallback when the entrypoint reference changes
		if (hasMounted.current) {
			resetFallback();
		}
	}, [entryPointReference, resetFallback]);

	useEffect(() => {
		hasMounted.current = true;

		// reset fallback on unmount
		return resetFallback;
	}, [resetFallback]);

	return (
		<Placeholder fallback={fallback} name={name} {...placeholderProps}>
			<span
				css={styles.root}
				data-ep-placeholder-id={name}
				{...(isPlaceholderForPageLayoutComponent && { 'data-layout-slot': 'true' })}
				{...(isSSRPlaceholderEnabled && {
					'data-vc': name,
				})}
				{...(isSSRPlaceholderEnabled &&
					__SERVER__ && {
						'data-ssr-placeholder': name,
					})}
				{...(isSSRPlaceholderEnabled &&
					!__SERVER__ && {
						'data-ssr-placeholder-replace': name,
					})}
			>
				{children}
			</span>
		</Placeholder>
	);
}

export const EntryPointReferencePlaceholder: React.FC<
	Omit<
		{
			name: string;
			children?: LazySuspenseProps['children'];
			fallback?: LazySuspenseProps['fallback'];
		} & {
			name: string;
			children?: LazySuspenseProps['children'];
			fallback?: LazySuspenseProps['fallback'];
		},
		'name'
	> & {
		name: string;
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		entryPointReference: PreloadedEntryPoint<any>;
		/**
		 * If the placeholder wraps a page layout component, we add a data-layout-slot="true" attribute.
		 * This matches what the Root component in `/atlassian-frontend-monorepo/platform/packages/design-system/navigation-system/src/ui/page-layout/root.tsx`
		 * expects to find on all of the page-layout components.
		 */
		isPlaceholderForPageLayoutComponent?: boolean;
		isSSRPlaceholderEnabled?: boolean;
	}
> = componentWithCondition(
	// useIsHydrateRenderMode isn't actually a hook and componentWithCondition is evaluated at render time :)
	() => useIsHydrateRenderMode() || (!__SERVER__ && !globalThis.__SPA__),
	NoContentPreservationEntryPointReferencePlaceholder,
	EntryPointReferencePlaceholderWithContentPreservation,
);
